/**
 * Daily Standup Service
 * Pure functions for validating standup submissions and formatting standup markdown for Slack.
 */

import { DailyGroup, DailyLine } from '../domain/types';
import { markdownToSlackMrkdwn } from '../domain/markdownToSlackMrkdwn';

/**
 * TipTap / GFM markdown for the editable message preview.
 * Line text is plain (or legacy TipTap markdown) and is embedded as list items.
 */
export function formatDailyComposerMarkdown(groups: DailyGroup[], channelName?: string): string {
  if (!groups || groups.length === 0) {
    return channelName
      ? `**Daily Standup** — #${channelName.replace(/^#/, '')}\n\n_Sin actualizaciones._`
      : '**Daily Standup**\n\n_Sin actualizaciones._';
  }

  const header = channelName
    ? `**Daily Standup** — #${channelName.replace(/^#/, '')}`
    : '**Daily Standup**';

  const sections: string[] = [];

  for (const group of groups) {
    const activeLines = group.lines.filter((line) => line.active && line.text.trim().length > 0);
    const hasDraft = group.draftText && group.draftText.trim().length > 0;

    if (activeLines.length === 0 && !hasDraft) {
      continue;
    }

    const linesFormatted = activeLines.map((line) => {
      const prefix = line.key ? `[${line.key}] ` : '';
      return `- ${prefix}${line.text.trim()}`;
    });

    if (hasDraft) {
      linesFormatted.push(`- ${group.draftText!.trim()}`);
    }

    sections.push(`**${group.title}**\n${linesFormatted.join('\n')}`);
  }

  if (sections.length === 0) {
    return `${header}\n\n_Sin actualizaciones seleccionadas._`;
  }

  return `${header}\n\n${sections.join('\n\n')}`;
}

/** Convert composer markdown to Slack mrkdwn (list markers → •). */
export function composerMarkdownToSlackMrkdwn(composerMarkdown: string): string {
  return markdownToSlackMrkdwn(composerMarkdown).replace(/(^|\n)[-*] /g, '$1• ');
}

/**
 * Formats daily standup groups into Slack mrkdwn for post / confirm.
 */
export function formatDailyMarkdown(groups: DailyGroup[], channelName?: string): string {
  return composerMarkdownToSlackMrkdwn(formatDailyComposerMarkdown(groups, channelName));
}

/**
 * Validates that at least one item across all standup groups is checked/active,
 * or that there is draft text entered.
 */
export function validateDaily(groups: DailyGroup[]): boolean {
  if (!groups || groups.length === 0) {
    return false;
  }

  return groups.some((group) => {
    const hasActiveLine = group.lines.some((line) => line.active && line.text.trim().length > 0);
    const hasDraft = typeof group.draftText === 'string' && group.draftText.trim().length > 0;
    return hasActiveLine || hasDraft;
  });
}

/**
 * Pure helper to toggle an item active state in standup groups
 */
export function toggleDailyLine(groups: DailyGroup[], groupId: string, lineId: string): DailyGroup[] {
  return groups.map((g) => {
    if (g.id !== groupId) return g;
    return {
      ...g,
      lines: g.lines.map((l) => (l.id === lineId ? { ...l, active: !l.active } : l)),
    };
  });
}

/**
 * Pure helper to add a custom line to a standup group
 */
export function addDailyCustomLine(
  groups: DailyGroup[],
  groupId: string,
  text: string,
  key?: string
): DailyGroup[] {
  if (!text.trim()) return groups;

  const newLine: DailyLine = {
    id: `custom-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    key: key?.trim() || '',
    text: text.trim(),
    active: true,
    isCustom: true,
  };

  return groups.map((g) => {
    if (g.id !== groupId) return g;
    return {
      ...g,
      lines: [...g.lines, newLine],
      draftText: '',
    };
  });
}

/**
 * Pure helper to update the text of an existing standup line (keeps key/link).
 */
export function updateDailyLine(
  groups: DailyGroup[],
  groupId: string,
  lineId: string,
  text: string
): DailyGroup[] {
  const trimmed = text.trim();
  if (!trimmed) return groups;

  return groups.map((g) => {
    if (g.id !== groupId) return g;
    return {
      ...g,
      lines: g.lines.map((l) => (l.id === lineId ? { ...l, text: trimmed } : l)),
    };
  });
}

/**
 * Pure helper to remove a standup line from a group.
 */
export function deleteDailyLine(
  groups: DailyGroup[],
  groupId: string,
  lineId: string
): DailyGroup[] {
  return groups.map((g) => {
    if (g.id !== groupId) return g;
    return {
      ...g,
      lines: g.lines.filter((l) => l.id !== lineId),
    };
  });
}

/**
 * Pure helper to attach a ticket/PR reference to an existing standup line.
 */
export function linkDailyLine(
  groups: DailyGroup[],
  groupId: string,
  lineId: string,
  key: string,
  link: string
): DailyGroup[] {
  const trimmedKey = key.trim();
  const trimmedLink = link.trim();
  if (!trimmedKey || !trimmedLink) return groups;

  return groups.map((g) => {
    if (g.id !== groupId) return g;
    return {
      ...g,
      lines: g.lines.map((l) =>
        l.id === lineId ? { ...l, key: trimmedKey, link: trimmedLink } : l
      ),
    };
  });
}

export function hasDailyLineReference(line: DailyLine): boolean {
  return Boolean(line.key?.trim());
}

export const DailyService = {
  formatDailyComposerMarkdown,
  composerMarkdownToSlackMrkdwn,
  formatDailyMarkdown,
  validateDaily,
  toggleDailyLine,
  addDailyCustomLine,
  updateDailyLine,
  deleteDailyLine,
  linkDailyLine,
  hasDailyLineReference,
};
